import React, { useState } from "react";
import styles from "./AlumniBenefits.module.css";
import { CornerUpRight } from "lucide-react";
import AlumniFormPopup from "../AlumniFormPopup/AlumniFormPopup";
import CustomiseCta from "../CustomiseCta/CustomiseCta";

interface BenefitItem {
  id: number;
  title: string;
  description: string | null;
}

interface CTA {
  id: number;
  type: string;
  button: {
    id: number;
    desktop_label: string;
    url: string | null;
    button_color: string | null;
    text_color: string | null;
    mobile_label: string;
  };
  icon_and_position: any;
}

interface HeadingBulletPoints {
  id: number;
  title: string | null;
  list: BenefitItem[];
}

interface TitleDescription {
  id: number;
  title: string;
  description: string;
}

interface CardWithImage {
  id: number;
  title_description: TitleDescription;
  images: string;
  mobiles_images: string;
}

interface RegisterAsAlumni {
  id: number;
  title: string;
  description: string;
  cta: CTA;
  cta_label: string;
  card_with_image: CardWithImage;
}

interface AlumniBenefitsData {
  id: number;
  title: string;
  description: string;
  heading_bullet_pts: HeadingBulletPoints;
  register_as_alumni: RegisterAsAlumni;
}

interface AlumniBenefitsProps {
  data: AlumniBenefitsData;
}

const AlumniBenefits: React.FC<AlumniBenefitsProps> = ({ data }) => {
  // State for popup visibility
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  // If no data is provided, don't render anything
  if (!data) return null;

  const title = data.title;
  const description = data.description;
  const ctaLabel = data.register_as_alumni.cta_label;
  const cta = data.register_as_alumni.cta;

  // Extract CTA button properties
  const buttonLabel = cta?.button?.desktop_label || ctaLabel;
  const buttonColor = cta?.button?.button_color || "#18463e";
  const textColor = cta?.button?.text_color || "#ffffff";
  const ctaType = cta?.type || "popup";

  // Handle popup open/close
  const openPopup = () => setIsPopupVisible(true);
  const closePopup = () => setIsPopupVisible(false);

  // Get benefits list from API
  const benefitsList = data?.heading_bullet_pts?.list.map(
    (item) => item?.title
  );

  return (
    <div className={styles.benefitsBG} id="benefit">
      <AlumniFormPopup
        data={data.register_as_alumni}
        show={isPopupVisible}
        onHide={closePopup}
        AlumniFormEnable
      />
      <div className="container">
        <section className={styles.alumniBenefitsSection}>
          <div className={styles.leftSection}>
            <h2 className={styles.heading}>{title}</h2>
            <p className={styles.description}>{description}</p>

            {ctaType === "pop-up" ? (
              <div className="d-none d-md-block">
                <div className={styles.registerButton}>
                  <button
                    style={{
                      backgroundColor: buttonColor || undefined,
                      color: textColor || undefined,
                    }}
                    onClick={openPopup}
                    data-interaction={buttonLabel}
                  >
                    {buttonLabel}
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.registerButton}>
                <CustomiseCta ctaData={cta} />
              </div>
            )}
          </div>
          <div className={styles.rightSection}>
            <div className={styles.benefitsGrid}>
              <ul>
                {benefitsList.map((benefit, idx) => (
                  <li key={idx} className={styles.benefitItem}>
                    <span className={styles.arrowIcon}>
                      <CornerUpRight />
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={`d-block d-md-none ${styles.mobileButtonContainer}`}>
            {ctaType === "pop-up" ? (
              <button
                className={styles.registerButton}
                style={{
                  backgroundColor: buttonColor || undefined,
                  color: textColor || undefined,
                }}
                onClick={openPopup}
                data-interaction={cta?.button?.mobile_label || buttonLabel}
              >
                {cta?.button?.mobile_label || buttonLabel}
              </button>
            ) : (
              <div className={styles.registerButton}>
                <CustomiseCta ctaData={cta} />
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AlumniBenefits;
